import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, UserPlus, Upload, Download, Trash2, Mail, 
  Loader2, Search, CheckCircle, Clock, ArrowLeft,
  FolderPlus, Pencil, ChevronDown, ChevronRight, ChevronLeft, GraduationCap,
  BookOpen, Star, Play, RefreshCw, History, Check, Minus, X,
  Eye, EyeOff, Minimize2, Maximize2, ChevronUp, Code, Copy, AlertTriangle, Sparkles
} from "lucide-react";
import QuestionCard from "../components/QuestionCard";
import CodeWorkspace from "../components/CodeWorkspace";
import { TrackBadge } from "../components/TrackBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { isBlockMode, modeLabel, normalizeMode, MODES, MODE_META } from "../lib/modes";

const API = "/api";

// Difficulty stars display component
function DifficultyStars({ difficulty }) {
  return (
    <span className="inline-flex items-center gap-0.5 ml-2">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-3 h-3 ${star <= difficulty ? "fill-amber-400 text-amber-400" : "text-gray-300"}`}
        />
      ))}
    </span>
  );
}

export default function ManageStudents({ user, authChecked }) {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [drills, setDrills] = useState([]);
  const [unassignedDrillId, setUnassignedDrillId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schoolName, setSchoolName] = useState("");
  const [activeCount, setActiveCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Single invite state
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteClassId, setInviteClassId] = useState("");
  const [inviting, setInviting] = useState(false);
  
  // Bulk upload state
  const [uploading, setUploading] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  
  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Class management state
  const [isClassDialogOpen, setIsClassDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [className, setClassName] = useState("");
  const [classDefaultMode, setClassDefaultMode] = useState("none");
  const [savingClass, setSavingClass] = useState(false);
  const [deleteClassTarget, setDeleteClassTarget] = useState(null);
  const [deletingClass, setDeletingClass] = useState(false);
  
  // Collapsed classes state
  const [collapsedClasses, setCollapsedClasses] = useState({});
  
  // Refresh drill progress state
  const [refreshingClass, setRefreshingClass] = useState(null);
  
  // Browse Drills section state
  const [browseDrillsExpanded, setBrowseDrillsExpanded] = useState(false);
  const [drillTrack, setDrillTrack] = useState("all");
  const drillsInTrack = drills.filter((d) => drillTrack === "all" || normalizeMode(d.mode) === drillTrack);
  const blocksDrillCount = drills.filter((d) => isBlockMode(d.mode)).length;
  const [assignDrillConfirm, setAssignDrillConfirm] = useState(null); // { drill, classId, className, currentDrillTitle }
  
  // Drill completion warning modal state
  const [completionWarning, setCompletionWarning] = useState({
    open: false,
    drill: null,
    classId: null,
    className: null,
    studentsCompleted: [],
    totalStudents: 0,
    checkingCompletion: false,
    recommendedDrill: null,
    loadingRecommendation: false,
    canRecommend: false
  });
  
  // Upgrade modal state (student slot limit reached)
  const [upgradeModal, setUpgradeModal] = useState({ open: false, info: null });
  const [planInfo, setPlanInfo] = useState(null);
  
  // Class History modal state
  const [classHistoryModal, setClassHistoryModal] = useState({
    open: false,
    classId: null,
    className: null
  });
  const [classHistory, setClassHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Email visibility state - tracks which emails are visible (not masked)
  const [visibleEmails, setVisibleEmails] = useState({});
  
  // Helper function to mask email - shows first 3 characters then masks the rest
  const maskEmail = (email) => {
    if (!email) return "";
    const [localPart, domain] = email.split("@");
    if (!domain) return email;
    const visibleChars = localPart.slice(0, 3);
    const maskedLocal = visibleChars + "•••";
    const [domainName, tld] = domain.split(".");
    const maskedDomain = domainName ? domainName.slice(0, 2) + "•••" : "•••";
    return `${maskedLocal}@${maskedDomain}${tld ? "." + tld : ""}`;
  };
  
  // Toggle email visibility for a specific student
  const toggleEmailVisibility = (studentId) => {
    setVisibleEmails(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  // Run Through Pane State
  const [runThroughPane, setRunThroughPane] = useState({
    open: false,
    minimized: false,
    drillId: null,
    drillTitle: null,
    drillDifficulty: null,
    classId: null,
    className: null
  });
  const [runThroughQuestions, setRunThroughQuestions] = useState([]);
  const [runThroughCurrentIndex, setRunThroughCurrentIndex] = useState(0);
  const [runThroughLoading, setRunThroughLoading] = useState(false);
  const [runThroughCompleted, setRunThroughCompleted] = useState([]);
  const [runThroughCodeCache, setRunThroughCodeCache] = useState({}); // Store code per question for session

  // Student Recent Code Pane State
  const [studentCodePane, setStudentCodePane] = useState({
    open: false,
    minimized: false,
    student: null, // { user_id, nickname, email }
    submissions: []
  });
  const [studentCodeLoading, setStudentCodeLoading] = useState(false);
  const [expandedStudentCode, setExpandedStudentCode] = useState(null);

  // Open Run Through Pane
  const openRunThrough = async (drillId, drillTitle, classId, className) => {
    setRunThroughPane({
      open: true,
      minimized: false,
      drillId,
      drillTitle,
      drillDifficulty: null,
      classId,
      className
    });
    setRunThroughLoading(true);
    setRunThroughQuestions([]);
    setRunThroughCurrentIndex(0);
    setRunThroughCompleted([]);
    
    try {
      const response = await fetch(`${API}/school/drill/${drillId}/preview`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        if (data.questions && data.questions.length > 0) {
          setRunThroughQuestions(data.questions);
          // Store drill difficulty from the response
          setRunThroughPane(prev => ({
            ...prev,
            drillDifficulty: data.drill?.difficulty || 3,
            drillMode: data.drill?.mode || "python"
          }));
        } else {
          toast.error("This drill has no questions");
          setRunThroughPane(prev => ({ ...prev, open: false }));
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to load drill questions");
        setRunThroughPane(prev => ({ ...prev, open: false }));
      }
    } catch (error) {
      console.error("Error loading drill:", error);
      toast.error("Failed to load drill");
      setRunThroughPane(prev => ({ ...prev, open: false }));
    } finally {
      setRunThroughLoading(false);
    }
  };

  // Close Run Through Pane - clears all code and state
  const closeRunThrough = () => {
    setRunThroughPane({
      open: false,
      minimized: false,
      drillId: null,
      drillTitle: null,
      drillDifficulty: null,
      classId: null,
      className: null
    });
    setRunThroughQuestions([]);
    setRunThroughCurrentIndex(0);
    setRunThroughCompleted([]);
    setRunThroughCodeCache({}); // Clear all cached code when closing
  };

  // Toggle Minimize Run Through Pane
  const toggleMinimizeRunThrough = () => {
    setRunThroughPane(prev => ({
      ...prev,
      minimized: !prev.minimized
    }));
  };

  // Handle question completion in run through
  // Handle question completion in run through - does NOT auto-advance for admin preview
  const handleRunThroughQuestionComplete = (questionId, result) => {
    // Mark as completed but stay on the same question
    // Admin can manually navigate to review other questions
    setRunThroughCompleted(prev => 
      prev.includes(questionId) ? prev : [...prev, questionId]
    );
  };

  // Open Student Recent Code Pane
  const openStudentCodePane = async (student) => {
    if (student.status !== "active") return; // Only active students have submissions
    
    setStudentCodePane({
      open: true,
      minimized: false,
      student: {
        user_id: student.user_id,
        nickname: student.nickname || student.name || student.email,
        email: student.email
      },
      submissions: []
    });
    setStudentCodeLoading(true);
    setExpandedStudentCode(null);
    
    try {
      const response = await fetch(`${API}/school/student/${student.user_id}/drill-submissions`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setStudentCodePane(prev => ({
          ...prev,
          submissions: data.submissions || []
        }));
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to load student code");
      }
    } catch (error) {
      console.error("Error loading student code:", error);
      toast.error("Failed to load student code");
    } finally {
      setStudentCodeLoading(false);
    }
  };

  // Close Student Code Pane
  const closeStudentCodePane = () => {
    setStudentCodePane({
      open: false,
      minimized: false,
      student: null,
      submissions: []
    });
    setExpandedStudentCode(null);
  };

  // Toggle Minimize Student Code Pane
  const toggleMinimizeStudentCode = () => {
    setStudentCodePane(prev => ({
      ...prev,
      minimized: !prev.minimized
    }));
  };

  useEffect(() => {
    // Wait until the auth check resolves before deciding to redirect,
    // otherwise a page refresh bounces logged-in school admins to home
    if (!authChecked) return;
    if (!user?.school_admin) {
      navigate("/");
      return;
    }
    fetchStudents();
    fetchDrills();
    refreshPlanInfo();
  }, [user, authChecked, navigate]);

  // Fetch the school's plan + student slot usage (for the header chip and caps)
  const refreshPlanInfo = async () => {
    try {
      const response = await fetch(`${API}/school/check-student-limit?adding_count=0`, {
        credentials: "include"
      });
      if (response.ok) {
        setPlanInfo(await response.json());
      }
    } catch (error) {
      console.error("Error fetching plan info:", error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await fetch(`${API}/school/students`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setStudents(data.students);
        setClasses(data.classes || []);
        setUnassignedDrillId(data.unassigned_drill_id || null);
        setSchoolName(data.school_name);
        setActiveCount(data.active_count);
        setPendingCount(data.pending_count);
        
        // Set default invite class to first available class
        if (data.classes?.length > 0 && !inviteClassId) {
          setInviteClassId(data.classes[0].class_id);
        }
      } else {
        toast.error("Failed to load students");
      }
    } catch (error) {
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  const fetchDrills = async () => {
    try {
      const response = await fetch(`${API}/school/drills`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setDrills(data.drills || []);
      }
    } catch (error) {
      console.error("Failed to fetch drills:", error);
    }
  };

  // Refresh drill progress for a specific class (or all students if no classId)
  const refreshDrillProgress = async (classId = null) => {
    setRefreshingClass(classId || "all");
    try {
      const response = await fetch(`${API}/school/students`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setStudents(data.students);
        setClasses(data.classes || []);
        toast.success("Drill progress refreshed");
      } else {
        toast.error("Failed to refresh drill progress");
      }
    } catch (error) {
      toast.error("Failed to refresh drill progress");
    } finally {
      setRefreshingClass(null);
    }
  };

  const handleAssignDrill = async (classId, drillId) => {
    try {
      const response = await fetch(`${API}/school/classes/${classId}/drill`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ drill_id: drillId || null })
      });
      
      if (response.ok) {
        toast.success(drillId ? "Drill assigned to class" : "Drill unassigned from class");
        fetchStudents(); // Refresh to get updated drill assignments
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to assign drill");
      }
    } catch (error) {
      toast.error("Failed to assign drill");
    }
  };

  // Check if students have completed all questions in a drill before assigning
  const checkDrillCompletion = async (drill, classId, className) => {
    setCompletionWarning({
      open: true,
      drill,
      classId,
      className,
      studentsCompleted: [],
      totalStudents: 0,
      checkingCompletion: true,
      recommendedDrill: null,
      loadingRecommendation: false,
      canRecommend: false
    });

    try {
      const response = await fetch(`${API}/school/classes/${classId}/check-drill-completion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ drill_id: drill.drill_id })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.all_clear) {
          // No students have completed all questions, proceed with assignment
          setCompletionWarning(prev => ({ ...prev, open: false }));
          handleAssignDrill(classId, drill.drill_id);
        } else {
          // Some students have completed all questions, show warning
          setCompletionWarning(prev => ({
            ...prev,
            checkingCompletion: false,
            studentsCompleted: data.students_completed,
            totalStudents: data.total_students,
            canRecommend: true
          }));
        }
      } else {
        toast.error("Failed to check drill completion");
        setCompletionWarning(prev => ({ ...prev, open: false }));
      }
    } catch (error) {
      toast.error("Error checking drill completion");
      setCompletionWarning(prev => ({ ...prev, open: false }));
    }
  };

  // Find a recommended alternative drill
  const findRecommendedDrill = async () => {
    if (!completionWarning.drill) return;
    
    setCompletionWarning(prev => ({ ...prev, loadingRecommendation: true }));

    try {
      const response = await fetch(`${API}/school/classes/${completionWarning.classId}/recommend-drill`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          min_difficulty: completionWarning.drill.difficulty || 1,
          exclude_drill_id: completionWarning.drill.drill_id
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCompletionWarning(prev => ({
          ...prev,
          loadingRecommendation: false,
          recommendedDrill: data.recommended_drill,
          canRecommend: !!data.recommended_drill
        }));
        
        if (!data.recommended_drill) {
          toast.info(data.reason || "No suitable alternative drill found");
        }
      } else {
        toast.error("Failed to find recommended drill");
        setCompletionWarning(prev => ({ ...prev, loadingRecommendation: false }));
      }
    } catch (error) {
      toast.error("Error finding recommended drill");
      setCompletionWarning(prev => ({ ...prev, loadingRecommendation: false }));
    }
  };

  // Assign the recommended drill
  const assignRecommendedDrill = () => {
    if (completionWarning.recommendedDrill) {
      handleAssignDrill(completionWarning.classId, completionWarning.recommendedDrill.drill_id);
      setCompletionWarning(prev => ({ ...prev, open: false }));
    }
  };

  // Assign anyway despite warning
  const assignDrillAnyway = () => {
    if (completionWarning.drill && completionWarning.classId) {
      handleAssignDrill(completionWarning.classId, completionWarning.drill.drill_id);
      setCompletionWarning(prev => ({ ...prev, open: false }));
    }
  };

  // Handle assigning drill from Browse Drills section
  const handleAssignDrillFromBrowse = async (drill, targetClass) => {
    // Check if class already has a drill assigned
    const currentDrill = drills.find(d => d.drill_id === targetClass.drill_id);
    
    if (currentDrill && currentDrill.drill_id !== drill.drill_id) {
      // Show confirmation modal for replacement
      setAssignDrillConfirm({
        drill,
        classId: targetClass.class_id,
        className: targetClass.name,
        currentDrillTitle: currentDrill.title
      });
    } else {
      // No existing drill or same drill - check for completion before assigning
      await checkDrillCompletion(drill, targetClass.class_id, targetClass.name);
    }
  };

  // Confirm drill replacement (also checks for completion)
  const confirmDrillAssignment = async () => {
    if (assignDrillConfirm) {
      const { drill, classId, className } = assignDrillConfirm;
      setAssignDrillConfirm(null);
      // Check for completion before assigning
      await checkDrillCompletion(drill, classId, className);
    }
  };

  // Fetch class drill history
  const fetchClassHistory = async (classId, className) => {
    setClassHistoryModal({ open: true, classId, className });
    setLoadingHistory(true);
    setClassHistory(null);
    
    try {
      const response = await fetch(`${API}/school/class/${classId}/history`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setClassHistory(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to load class history");
        setClassHistoryModal({ open: false, classId: null, className: null });
      }
    } catch (error) {
      toast.error("Failed to load class history");
      setClassHistoryModal({ open: false, classId: null, className: null });
    } finally {
      setLoadingHistory(false);
    }
  };

  // Check if adding students would exceed the school's student slot limit
  const checkStudentLimit = async (addingCount = 1) => {
    try {
      const response = await fetch(`${API}/school/check-student-limit?adding_count=${addingCount}`, {
        credentials: "include"
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error("Error checking student limit:", error);
    }
    return { limit_reached: false };
  };

  // Execute single invite (after limit check passes)
  const executeInvite = async (email, classId) => {
    setInviting(true);
    try {
      const response = await fetch(`${API}/school/students/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          email: email.trim().toLowerCase(),
          class_id: classId || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.added_immediately) {
          toast.success(`${email} added to school (existing user)`);
        } else {
          toast.success(data.message);
        }
        setInviteEmail("");
        fetchStudents();
        refreshPlanInfo();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    
    // Check student limit first
    const limitCheck = await checkStudentLimit(1);
    
    if (limitCheck.limit_reached) {
      setUpgradeModal({ open: true, info: limitCheck });
      return;
    }
    
    await executeInvite(inviteEmail, inviteClassId);
  };

  // Execute bulk upload (after limit check passes)
  const executeBulkUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${API}/school/students/bulk-invite`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const messages = [];
        if (data.added_count > 0) messages.push(`${data.added_count} existing users added`);
        if (data.invited_count > 0) messages.push(`${data.invited_count} invitations sent`);
        toast.success(messages.join(", ") || "Processed successfully");
        if (data.failed_count > 0) {
          toast.warning(`${data.failed_count} emails failed: ${data.failed_emails.slice(0, 3).join(", ")}${data.failed_emails.length > 3 ? "..." : ""}`);
        }
        fetchStudents();
        refreshPlanInfo();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Bulk upload failed");
      }
    } catch (error) {
      toast.error("Bulk upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Count lines in CSV to estimate how many students will be added
    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target.result;
      const lines = content.split('\n').filter(line => line.trim() && !line.toLowerCase().startsWith('email'));
      const estimatedCount = lines.length;
      
      // Check student limit
      const limitCheck = await checkStudentLimit(estimatedCount);
      
      if (limitCheck.limit_reached) {
        setUpgradeModal({ open: true, info: limitCheck });
        e.target.value = "";
        return;
      }
      
      await executeBulkUpload(file);
      e.target.value = "";
    };
    reader.readAsText(file);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const identifier = deleteTarget.user_id || deleteTarget.email || deleteTarget.invite_token;
      const response = await fetch(`${API}/school/students/${encodeURIComponent(identifier)}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success(deleteTarget.status === "pending" ? "Invitation cancelled" : "Student removed");
        fetchStudents();
        refreshPlanInfo();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to remove");
      }
    } catch (error) {
      toast.error("Failed to remove");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleResendInvite = async (email) => {
    try {
      const response = await fetch(`${API}/school/students/${encodeURIComponent(email)}/resend-invite`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Invitation resent");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to resend");
      }
    } catch (error) {
      toast.error("Failed to resend invitation");
    }
  };

  // Class management functions
  const openCreateClass = () => {
    setEditingClass(null);
    setClassName("");
    setClassDefaultMode("none");
    setIsClassDialogOpen(true);
  };

  const openEditClass = (cls) => {
    setEditingClass(cls);
    setClassName(cls.name);
    setClassDefaultMode(cls.default_mode || "none");
    setIsClassDialogOpen(true);
  };

  const handleSaveClass = async () => {
    if (!className.trim()) {
      toast.error("Please enter a class name");
      return;
    }
    
    setSavingClass(true);
    try {
      const url = editingClass 
        ? `${API}/school/classes/${editingClass.class_id}`
        : `${API}/school/classes`;
      
      const response = await fetch(url, {
        method: editingClass ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: className.trim(), default_mode: classDefaultMode === "none" ? "" : classDefaultMode })
      });
      
      if (response.ok) {
        toast.success(editingClass ? "Class updated" : "Class created");
        setIsClassDialogOpen(false);
        fetchStudents();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save class");
      }
    } catch (error) {
      toast.error("Failed to save class");
    } finally {
      setSavingClass(false);
    }
  };

  const handleDeleteClass = async () => {
    if (!deleteClassTarget) return;
    
    setDeletingClass(true);
    try {
      const response = await fetch(`${API}/school/classes/${deleteClassTarget.class_id}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Class deleted");
        fetchStudents();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete class");
      }
    } catch (error) {
      toast.error("Failed to delete class");
    } finally {
      setDeletingClass(false);
      setDeleteClassTarget(null);
    }
  };

  const handleUpdateStudentClass = async (student, newClassId) => {
    const identifier = student.user_id || student.invite_token || student.email;
    try {
      const response = await fetch(`${API}/school/students/${encodeURIComponent(identifier)}/class`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ class_id: newClassId || null })
      });
      
      if (response.ok) {
        toast.success("Student class updated");
        fetchStudents();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update class");
      }
    } catch (error) {
      toast.error("Failed to update class");
    }
  };

  const toggleClassCollapse = (classId) => {
    setCollapsedClasses(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  // Filter students by search query
  const filteredStudents = students.filter(s => 
    (s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
     s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
     s.nickname?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Group students by class
  const getStudentsByClass = () => {
    const grouped = {};
    
    // Initialize with existing classes
    classes.forEach(cls => {
      grouped[cls.class_id] = {
        class_id: cls.class_id,
        name: cls.name,
        drill_id: cls.drill_id,
        default_mode: cls.default_mode || null,
        students: []
      };
    });
    
    // Add "Unassigned" group
    grouped["unassigned"] = {
      class_id: "unassigned",
      name: "Unassigned",
      drill_id: unassignedDrillId,
      students: []
    };
    
    // Sort students into groups
    filteredStudents.forEach(student => {
      const classId = student.class_id || "unassigned";
      if (grouped[classId]) {
        grouped[classId].students.push(student);
      } else {
        grouped["unassigned"].students.push(student);
      }
    });
    
    // Convert to array and sort: named classes first (alphabetically), then Unassigned
    const result = Object.values(grouped)
      .filter(g => g.class_id !== "unassigned")
      .sort((a, b) => a.name.localeCompare(b.name));
    
    // Add Unassigned at the end
    result.push(grouped["unassigned"]);
    
    return result;
  };

  const groupedStudents = getStudentsByClass();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            className="mb-4 gap-2"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground" data-testid="page-title">
                Manage Students
              </h1>
              <p className="text-muted-foreground mt-1">{schoolName}</p>
            </div>
            
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 text-green-600 dark:text-green-400">
                <CheckCircle className="w-4 h-4" />
                <span>{activeCount} Active</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
                <Clock className="w-4 h-4" />
                <span>{pendingCount} Pending</span>
              </div>
              {planInfo && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 text-primary" data-testid="plan-usage-chip">
                  <Users className="w-4 h-4" />
                  <span>
                    {planInfo.has_invoices_email && planInfo.plan === "starter"
                      ? `${planInfo.current_students} students · ${planInfo.plan_label || "Teacher Starter"}`
                      : `${planInfo.current_students}/${planInfo.student_limit} slots · ${planInfo.plan_label || "Teacher Starter"}`}
                  </span>
                </div>
              )}
              {planInfo && planInfo.plan !== "teacher_pro" && (
                <Button
                  size="sm"
                  className="gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:opacity-90 text-white"
                  onClick={() => navigate("/subscription?tab=teacher")}
                  data-testid="upgrade-plan-header-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  Upgrade
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Classes Section */}
        <div className="bg-card rounded-xl border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-primary" />
              Classes
            </h2>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={openCreateClass}
              className="gap-2"
              data-testid="create-class-btn"
            >
              <FolderPlus className="w-4 h-4" />
              Create Class
            </Button>
          </div>
          
          {classes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No classes created yet. Create classes to organize your students.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {classes.map(cls => (
                <div 
                  key={cls.class_id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-border group"
                >
                  <span className="text-sm font-medium">{cls.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({filteredStudents.filter(s => s.class_id === cls.class_id).length})
                  </span>
                  {cls.default_mode && <TrackBadge mode={cls.default_mode} showPython testId={`class-default-mode-${cls.class_id}`} />}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => openEditClass(cls)}
                    data-testid={`edit-class-${cls.class_id}`}
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
                    onClick={() => setDeleteClassTarget(cls)}
                    data-testid={`delete-class-${cls.class_id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Invite Section */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Single Invite */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Invite Student
            </h2>
            <form onSubmit={handleInvite} className="space-y-3">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="student@email.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="flex-1"
                  data-testid="invite-email-input"
                />
              </div>
              <div className="flex gap-2">
                <Select 
                  value={inviteClassId} 
                  onValueChange={setInviteClassId}
                >
                  <SelectTrigger className="flex-1" data-testid="invite-class-select">
                    <SelectValue placeholder="Select class (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {classes.map(cls => (
                      <SelectItem key={cls.class_id} value={cls.class_id}>
                        {cls.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="submit" disabled={inviting || !inviteEmail.trim()} data-testid="send-invite-btn">
                  {inviting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Bulk Upload */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              Bulk Upload
            </h2>
            <div className="flex items-center gap-4">
              <label className="flex-1">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleBulkUpload}
                  className="hidden"
                  disabled={uploading}
                  data-testid="csv-upload-input"
                />
                <div className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-border hover:border-primary/50 cursor-pointer transition-colors">
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>Upload CSV</span>
                    </>
                  )}
                </div>
              </label>
              <a
                href={`${API}/school/csv-template`}
                className="flex items-center gap-2 text-sm text-primary hover:underline"
                data-testid="download-template-link"
              >
                <Download className="w-4 h-4" />
                Template
              </a>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              CSV format: email, class (optional). New classes are created automatically.
            </p>
          </div>
        </div>

        {/* Browse Drills Section */}
        <div className="mb-6 bg-card rounded-xl border border-border overflow-hidden">
          <button
            onClick={() => setBrowseDrillsExpanded(!browseDrillsExpanded)}
            className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
            data-testid="browse-drills-toggle"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              <span className="font-medium">Browse Drills</span>
              <span className="text-sm text-muted-foreground">
                ({drills.length} available)
              </span>
            </div>
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${browseDrillsExpanded ? 'rotate-180' : ''}`} />
          </button>
          
          {browseDrillsExpanded && (
            <div className="border-t border-border max-h-96 overflow-y-auto">
              {blocksDrillCount > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/20" data-testid="school-drill-track-filter">
                  <span className="text-xs text-muted-foreground">Show:</span>
                  {[{ v: "all", label: "All" }, ...MODES.map((v) => ({ v, label: MODE_META[v].label }))].map(({ v, label }) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setDrillTrack(v)}
                      className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${drillTrack === v ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}
                      data-testid={`school-drill-track-${v}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {drillsInTrack.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No {drillTrack === "all" ? "" : `${modeLabel(drillTrack)} `}drills available yet</p>
                  <p className="text-sm">Contact your administrator to create drills</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {drillsInTrack.map((drill) => (
                    <div key={drill.drill_id} className="p-4 hover:bg-muted/30 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-foreground">{drill.title}</h4>
                            <TrackBadge mode={drill.mode} testId={`school-drill-track-badge-${drill.drill_id}`} />
                            <DifficultyStars difficulty={drill.difficulty || 3} />
                            <span className="text-xs text-muted-foreground">
                              ({drill.question_count || drill.question_ids?.length || 0} questions)
                            </span>
                          </div>
                          {drill.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {drill.description}
                            </p>
                          )}
                        </div>
                        
                        {/* Assign to Class Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0 gap-2"
                              data-testid={`assign-drill-${drill.drill_id}`}
                            >
                              <GraduationCap className="w-4 h-4" />
                              Assign to Class
                              <ChevronDown className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {classes.length === 0 ? (
                              <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                                No classes created yet
                              </div>
                            ) : (
                              classes.map((cls) => {
                                const isCurrentlyAssigned = cls.drill_id === drill.drill_id;
                                const hasOtherDrill = cls.drill_id && cls.drill_id !== drill.drill_id;
                                return (
                                  <DropdownMenuItem
                                    key={cls.class_id}
                                    onClick={() => handleAssignDrillFromBrowse(drill, cls)}
                                    className={isCurrentlyAssigned ? "bg-primary/10" : ""}
                                    data-testid={`assign-${drill.drill_id}-to-${cls.class_id}`}
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <span>{cls.name}</span>
                                      {isCurrentlyAssigned && (
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                      )}
                                      {hasOtherDrill && (
                                        <span className="text-xs text-amber-500">Has drill</span>
                                      )}
                                    </div>
                                  </DropdownMenuItem>
                                );
                              })
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Student Lists by Class */}
        <div className="space-y-4">
          {groupedStudents.map(group => {
            const isCollapsed = collapsedClasses[group.class_id];
            const hasStudents = group.students.length > 0;
            const assignedDrillId = group.class_id === "unassigned" 
              ? unassignedDrillId 
              : group.drill_id;
            const assignedDrill = drills.find(d => d.drill_id === assignedDrillId);
            
            return (
              <div 
                key={group.class_id} 
                className="bg-card rounded-xl border border-border overflow-hidden"
              >
                {/* Class Header */}
                <div 
                  className="flex items-center justify-between p-4 bg-muted/30"
                  data-testid={`class-header-${group.class_id}`}
                >
                  <div 
                    className="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => toggleClassCollapse(group.class_id)}
                  >
                    {isCollapsed ? (
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                    <h3 className="font-semibold text-foreground">{group.name}</h3>
                    <span className="text-sm text-muted-foreground">
                      ({group.students.length} student{group.students.length !== 1 ? "s" : ""})
                    </span>
                    {group.default_mode && <TrackBadge mode={group.default_mode} showPython testId={`class-header-mode-${group.class_id}`} />}
                  </div>
                  
                  {/* Drill Assignment Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2 shrink-0"
                        data-testid={`assign-drill-${group.class_id}`}
                      >
                        <BookOpen className="w-4 h-4" />
                        {assignedDrill ? (
                          <span className="flex items-center gap-1">
                            {assignedDrill.title}
                            <TrackBadge mode={assignedDrill.mode} />
                            <DifficultyStars difficulty={assignedDrill.difficulty} />
                          </span>
                        ) : (
                          "Assign Drill"
                        )}
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[280px]">
                      {drills.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                          No drills available
                        </div>
                      ) : (
                        <>
                          {drills.map(drill => (
                            <DropdownMenuItem
                              key={drill.drill_id}
                              onClick={() => handleAssignDrill(group.class_id, drill.drill_id)}
                              className={`flex items-center justify-between cursor-pointer ${
                                assignedDrillId === drill.drill_id ? "bg-primary/10" : ""
                              }`}
                              data-testid={`drill-option-${drill.drill_id}`}
                            >
                              <span className="flex-1 truncate">{drill.title}</span>
                              <TrackBadge mode={drill.mode} className="mr-1.5" />
                              <DifficultyStars difficulty={drill.difficulty} />
                            </DropdownMenuItem>
                          ))}
                          {assignedDrill && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleAssignDrill(group.class_id, null)}
                                className="text-destructive cursor-pointer"
                                data-testid={`unassign-drill-${group.class_id}`}
                              >
                                Remove Drill Assignment
                              </DropdownMenuItem>
                            </>
                          )}
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  {/* Run Through Drill Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className={`gap-2 shrink-0 ml-2 ${
                      assignedDrill 
                        ? "text-orange-600 border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-500/10" 
                        : "opacity-50 cursor-not-allowed"
                    }`}
                    disabled={!assignedDrill}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (assignedDrill) {
                        // Open run through pane instead of navigating
                        openRunThrough(
                          assignedDrill.drill_id, 
                          assignedDrill.title,
                          group.class_id,
                          group.name
                        );
                      }
                    }}
                    data-testid={`run-drill-${group.class_id}`}
                    title={assignedDrill ? `Preview: ${assignedDrill.title}` : "Assign a drill first"}
                  >
                    <Play className="w-4 h-4" />
                    <span className="hidden sm:inline">Run Through</span>
                  </Button>
                  
                  {/* Refresh Drill Progress Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-1 shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      refreshDrillProgress(group.class_id);
                    }}
                    disabled={refreshingClass === group.class_id || refreshingClass === "all"}
                    data-testid={`refresh-drill-${group.class_id}`}
                    title="Refresh drill progress"
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshingClass === group.class_id ? 'animate-spin' : ''}`} />
                  </Button>
                  
                  {/* Class History Button */}
                  {group.class_id !== "unassigned" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-1 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        fetchClassHistory(group.class_id, group.name);
                      }}
                      data-testid={`class-history-${group.class_id}`}
                      title="View class drill history"
                    >
                      <History className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Student Table */}
                {!isCollapsed && (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid={`students-table-${group.class_id}`}>
                      <thead>
                        <tr className="border-b border-border bg-muted/20">
                          <th className="text-left p-4 font-medium text-muted-foreground">Student</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Class</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Drill Progress</th>
                          <th className="text-right p-4 font-medium text-muted-foreground">Weekly</th>
                          <th className="text-right p-4 font-medium text-muted-foreground">Total</th>
                          <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!hasStudents ? (
                          <tr>
                            <td colSpan={7} className="p-6 text-center text-muted-foreground">
                              {group.class_id === "unassigned" 
                                ? "No unassigned students"
                                : "No students in this class"
                              }
                            </td>
                          </tr>
                        ) : (
                          group.students.map((student, index) => {
                            const studentKey = student.user_id || student.email || index;
                            const isEmailVisible = visibleEmails[studentKey];
                            const displayEmail = isEmailVisible ? student.email : maskEmail(student.email);
                            
                            return (
                            <tr 
                              key={studentKey} 
                              className="border-b border-border last:border-0 hover:bg-muted/20"
                            >
                              <td className="p-4">
                                <div>
                                  <button
                                    onClick={() => student.status === "active" && openStudentCodePane(student)}
                                    className={`font-medium text-left ${
                                      student.status === "active" 
                                        ? "text-primary hover:underline cursor-pointer" 
                                        : "text-foreground cursor-default"
                                    }`}
                                    disabled={student.status !== "active"}
                                    title={student.status === "active" ? "View recent code" : ""}
                                    data-testid={`student-name-${studentKey}`}
                                  >
                                    {student.nickname || student.name || displayEmail}
                                  </button>
                                  {(student.nickname || student.name) && (
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-sm text-muted-foreground font-mono">
                                        {displayEmail}
                                      </p>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleEmailVisibility(studentKey);
                                        }}
                                        className="p-0.5 rounded hover:bg-muted transition-colors"
                                        title={isEmailVisible ? "Hide email" : "Show email"}
                                        data-testid={`toggle-email-${studentKey}`}
                                      >
                                        {isEmailVisible ? (
                                          <EyeOff className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                                        ) : (
                                          <Eye className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="p-4">
                                {student.status === "active" ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-600 dark:text-green-400">
                                    <CheckCircle className="w-3 h-3" />
                                    Active
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400">
                                    <Clock className="w-3 h-3" />
                                    Pending
                                  </span>
                                )}
                              </td>
                              <td className="p-4">
                                <Select
                                  value={student.class_id || "unassigned"}
                                  onValueChange={(val) => handleUpdateStudentClass(student, val === "unassigned" ? null : val)}
                                >
                                  <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`student-class-select-${student.email || student.user_id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="unassigned">Unassigned</SelectItem>
                                    {classes.map(cls => (
                                      <SelectItem key={cls.class_id} value={cls.class_id}>
                                        {cls.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="p-4">
                                {student.drill_progress ? (
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 min-w-[80px]">
                                      <div className="flex items-center justify-between text-xs mb-1">
                                        <span className={`font-medium ${
                                          student.drill_progress.status === "completed" 
                                            ? "text-green-600" 
                                            : "text-orange-600"
                                        }`}>
                                          {student.drill_progress.completed}/{student.drill_progress.total}
                                        </span>
                                        {student.drill_progress.status === "completed" && (
                                          <CheckCircle className="w-3 h-3 text-green-500" />
                                        )}
                                      </div>
                                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                        <div 
                                          className={`h-full transition-all ${
                                            student.drill_progress.status === "completed"
                                              ? "bg-green-500"
                                              : "bg-orange-500"
                                          }`}
                                          style={{ 
                                            width: `${student.drill_progress.total > 0 ? (student.drill_progress.completed / student.drill_progress.total) * 100 : 0}%` 
                                          }}
                                        />
                                      </div>
                                      <p className="text-[10px] text-muted-foreground mt-0.5 truncate max-w-[100px]" title={student.drill_progress.drill_title}>
                                        {student.drill_progress.drill_title || "Drill"}
                                      </p>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="p-4 text-right font-mono text-sm">
                                {student.weekly_score || 0}
                              </td>
                              <td className="p-4 text-right font-mono text-sm">
                                {student.total_score || 0}
                              </td>
                              <td className="p-4 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {student.status === "pending" && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleResendInvite(student.email)}
                                      title="Resend invitation"
                                      data-testid={`resend-btn-${student.email}`}
                                    >
                                      <Mail className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteTarget(student)}
                                    className="text-destructive hover:text-destructive"
                                    title={student.status === "pending" ? "Cancel invitation" : "Remove student"}
                                    data-testid={`delete-btn-${student.email || student.user_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );})
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Class Create/Edit Dialog */}
      <Dialog open={isClassDialogOpen} onOpenChange={setIsClassDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingClass ? "Edit Class" : "Create Class"}</DialogTitle>
            <DialogDescription>Students in a class open CodingDrills in the class's coding mode. They can still switch modes from the header.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="class-name-input">Class name</Label>
              <Input
                id="class-name-input"
                placeholder="Class name (e.g., Year 7A)"
                value={className}
                onChange={(e) => setClassName(e.target.value)}
                data-testid="class-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Default coding mode</Label>
              <Select value={classDefaultMode} onValueChange={setClassDefaultMode}>
                <SelectTrigger data-testid="class-default-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="class-default-mode-none">No default (students choose)</SelectItem>
                  {MODES.map((m) => (
                    <SelectItem key={m} value={m} data-testid={`class-default-mode-${m}`}>{MODE_META[m].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsClassDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveClass} disabled={savingClass} data-testid="save-class-btn">
              {savingClass ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingClass ? "Update" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Class Confirmation Dialog */}
      <AlertDialog open={!!deleteClassTarget} onOpenChange={() => setDeleteClassTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Class?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the class "{deleteClassTarget?.name}". Students in this class will become unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingClass}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClass}
              disabled={deletingClass}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingClass ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Student Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.status === "pending" ? "Cancel Invitation?" : "Remove Student?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.status === "pending" 
                ? `This will cancel the invitation sent to ${deleteTarget?.email}.`
                : `This will remove ${deleteTarget?.nickname || deleteTarget?.name || deleteTarget?.email} from your school. They will lose access to school features.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Replace Drill Confirmation Modal */}
      <AlertDialog open={!!assignDrillConfirm} onOpenChange={() => setAssignDrillConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace Current Drill?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                <strong>{assignDrillConfirm?.className}</strong> already has a drill assigned:
              </p>
              <p className="text-amber-600 dark:text-amber-400 font-medium">
                "{assignDrillConfirm?.currentDrillTitle}"
              </p>
              <p>
                Do you want to replace it with:
              </p>
              <p className="text-primary font-medium">
                "{assignDrillConfirm?.drill?.title}"
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Note: This will reset all student progress on the current drill.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDrillAssignment}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Replace Drill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upgrade Plan Modal (student slot limit reached) */}
      <Dialog open={upgradeModal.open} onOpenChange={(open) => !open && setUpgradeModal({ open: false, info: null })}>
        <DialogContent className="sm:max-w-md" data-testid="upgrade-plan-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Sparkles className="w-6 h-6 text-primary" />
              Student Limit Reached
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <p className="text-muted-foreground">
              You're using {upgradeModal.info?.current_students} of {upgradeModal.info?.student_limit} student
              slots on the <span className="font-semibold text-foreground">{upgradeModal.info?.plan_label || "Teacher Starter"}</span> plan.
            </p>

            {upgradeModal.info?.plan === "teacher_pro" ? (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-foreground">
                  You're on our largest plan. Contact us via the contact form for a custom school license.
                </p>
              </div>
            ) : (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-2">
                {upgradeModal.info?.plan === "starter" && (
                  <p className="text-sm text-foreground">
                    <span className="font-bold text-primary">Teacher Plus ($14/mo)</span> — 50 student slots &amp; live drills with 20 players
                  </p>
                )}
                <p className="text-sm text-foreground">
                  <span className="font-bold text-primary">Teacher Pro ($29/mo)</span> — 200 student slots &amp; live drills with 50 players
                </p>
                <p className="text-xs text-muted-foreground">Instant activation — no application needed.</p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setUpgradeModal({ open: false, info: null })}
              data-testid="upgrade-modal-close-btn"
            >
              Not Now
            </Button>
            {upgradeModal.info?.plan !== "teacher_pro" && (
              <Button
                onClick={() => navigate("/subscription?tab=teacher")}
                className="gap-2"
                data-testid="upgrade-plan-btn"
              >
                <Sparkles className="w-4 h-4" />
                View Upgrade Options
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Class History Modal */}
      <Dialog 
        open={classHistoryModal.open} 
        onOpenChange={(open) => !open && setClassHistoryModal({ open: false, classId: null, className: null })}
      >
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Class History: {classHistoryModal.className}
            </DialogTitle>
            <DialogDescription>
              View drill completion history for all students in this class
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : !classHistory ? (
              <div className="text-center py-12 text-muted-foreground">
                Failed to load history
              </div>
            ) : classHistory.drills.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No drill history for this class yet</p>
                <p className="text-sm mt-1">Assign a drill to get started</p>
              </div>
            ) : classHistory.students.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No students in this class</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm z-10">
                    <tr>
                      <th className="text-left p-3 font-medium border-b border-r sticky left-0 bg-muted/80 min-w-[150px]">
                        Student
                      </th>
                      {classHistory.drills.map((drill) => (
                        <th 
                          key={drill.drill_id} 
                          className="text-center p-3 font-medium border-b min-w-[120px]"
                        >
                          <div className="flex flex-col items-center gap-1">
                            <span className="truncate max-w-[100px]" title={drill.title}>
                              {drill.title}
                            </span>
                            <TrackBadge mode={drill.mode} />
                            {drill.date && (
                              <span className="text-xs text-muted-foreground font-normal">
                                {new Date(drill.date).toLocaleDateString("en-AU", {
                                  day: "numeric",
                                  month: "short"
                                })}
                              </span>
                            )}
                            {drill.is_current && (
                              <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                                Current
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {classHistory.students.map((student, idx) => (
                      <tr key={student.user_id} className={idx % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                        <td 
                          className="p-3 font-medium border-r sticky left-0 bg-inherit cursor-help"
                          title={student.email ? `Email: ${student.email}` : undefined}
                        >
                          {student.nickname}
                        </td>
                        {student.drills.map((drillStatus, drillIdx) => (
                          <td key={drillIdx} className="p-3 text-center">
                            {drillStatus.status === "completed" ? (
                              <div className="flex items-center justify-center">
                                <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-500/20 flex items-center justify-center">
                                  <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                              </div>
                            ) : drillStatus.status === "partial" ? (
                              <div className="flex items-center justify-center">
                                <div className="px-2 py-1 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 font-medium text-xs">
                                  {drillStatus.completed}/{drillStatus.total}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                                  <Minus className="w-5 h-5 text-red-600 dark:text-red-400" />
                                </div>
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Legend */}
          {classHistory && classHistory.drills.length > 0 && classHistory.students.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t text-sm text-muted-foreground">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-500/20 flex items-center justify-center">
                    <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <span>Completed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 font-medium text-xs">
                    X/Y
                  </div>
                  <span>Partial</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                    <Minus className="w-4 h-4 text-red-600 dark:text-red-400" />
                  </div>
                  <span>Not started</span>
                </div>
              </div>
              {classHistory.max_drill_history && classHistory.drills.length >= classHistory.max_drill_history && (
                <span className="text-xs text-muted-foreground/70">
                  Showing last {classHistory.max_drill_history} drills
                </span>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setClassHistoryModal({ open: false, classId: null, className: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drill Completion Warning Modal */}
      <Dialog 
        open={completionWarning.open} 
        onOpenChange={(open) => !open && setCompletionWarning(prev => ({ ...prev, open: false }))}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Students Have Completed This Drill
            </DialogTitle>
            <DialogDescription>
              The following students have already completed all questions in <strong>{completionWarning.drill?.title}</strong>:
            </DialogDescription>
          </DialogHeader>
          
          {completionWarning.checkingCompletion ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Checking student progress...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* List of students who completed all questions */}
              <div className="max-h-48 overflow-y-auto border rounded-lg p-3 bg-muted/30">
                {completionWarning.studentsCompleted.map((student, idx) => (
                  <div key={student.user_id} className="flex items-center gap-2 py-1.5 border-b last:border-b-0">
                    <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span className="font-medium text-sm">{student.nickname}</span>
                    <span className="text-xs text-muted-foreground truncate">({student.email})</span>
                  </div>
                ))}
              </div>
              
              <p className="text-sm text-muted-foreground">
                {completionWarning.studentsCompleted.length} of {completionWarning.totalStudents} students have completed all questions in this drill before.
              </p>

              {/* Recommended drill section */}
              {completionWarning.recommendedDrill && (
                <div className="p-3 border rounded-lg bg-emerald-50 dark:bg-emerald-950/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium text-sm text-emerald-700 dark:text-emerald-400">Recommended Alternative</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{completionWarning.recommendedDrill.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {completionWarning.recommendedDrill.question_count} questions • 
                        {" "}{Array(completionWarning.recommendedDrill.difficulty || 3).fill("★").join("")} difficulty
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setCompletionWarning(prev => ({ ...prev, open: false }))}
              className="sm:mr-auto"
            >
              Cancel
            </Button>
            
            {!completionWarning.recommendedDrill && !completionWarning.loadingRecommendation && (
              <Button
                variant="secondary"
                onClick={findRecommendedDrill}
                disabled={completionWarning.checkingCompletion}
                className="gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Recommend Drill
              </Button>
            )}
            
            {completionWarning.loadingRecommendation && (
              <Button variant="secondary" disabled className="gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Finding...
              </Button>
            )}
            
            {completionWarning.recommendedDrill && (
              <Button
                variant="default"
                onClick={assignRecommendedDrill}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Sparkles className="w-4 h-4" />
                Use Recommended
              </Button>
            )}
            
            <Button
              variant="destructive"
              onClick={assignDrillAnyway}
              disabled={completionWarning.checkingCompletion}
              data-testid="assign-drill-anyway-btn"
            >
              Assign Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run Through Floating Pane */}
      {runThroughPane.open && (
        <div 
          className={`fixed z-50 bg-background border rounded-lg shadow-2xl transition-all duration-300 ${
            runThroughPane.minimized 
              ? "bottom-4 right-4 w-80 h-14" 
              : "inset-4 md:inset-8 lg:left-[15%] lg:right-[15%] lg:top-8 lg:bottom-8"
          }`}
          style={{ 
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)" 
          }}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-3 border-b bg-muted/30 relative z-10 ${runThroughPane.minimized ? "rounded-lg" : "rounded-t-lg"}`}>
            <div className="flex items-center gap-2 min-w-0">
              <BookOpen className="w-5 h-5 text-orange-500 shrink-0" />
              <div className="min-w-0">
                <h3 className="font-semibold text-sm truncate flex items-center gap-1.5">
                  {runThroughPane.drillTitle || "Run Through"}
                  <TrackBadge mode={runThroughPane.drillMode} testId="run-through-blocks-badge" />
                </h3>
                {!runThroughPane.minimized && (
                  <p className="text-xs text-muted-foreground truncate">
                    {runThroughPane.className} • {runThroughQuestions.length} questions
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {!runThroughPane.minimized && runThroughQuestions.length > 0 && (
                <span className="text-xs text-muted-foreground mr-2">
                  {runThroughCurrentIndex + 1} / {runThroughQuestions.length}
                </span>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={toggleMinimizeRunThrough}
                title={runThroughPane.minimized ? "Maximize" : "Minimize"}
              >
                {runThroughPane.minimized ? (
                  <Maximize2 className="w-4 h-4" />
                ) : (
                  <Minimize2 className="w-4 h-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={closeRunThrough}
                title="Close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Content (hidden when minimized) */}
          {!runThroughPane.minimized && (
            <div className="flex flex-col h-[calc(100%-56px)] overflow-hidden">
              {runThroughLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : runThroughQuestions.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  No questions in this drill
                </div>
              ) : (
                <>
                  {/* Progress Bar */}
                  <div className="px-4 py-2 border-b bg-muted/10">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500"
                          style={{ width: `${((runThroughCurrentIndex + 1) / runThroughQuestions.length) * 100}%` }}
                        />
                      </div>
                      <div className="flex gap-1">
                        {runThroughQuestions.map((q, idx) => (
                          <button
                            key={q.question_id || idx}
                            onClick={() => setRunThroughCurrentIndex(idx)}
                            className={`w-6 h-6 rounded-full text-xs font-medium transition-all ${
                              idx === runThroughCurrentIndex
                                ? "bg-orange-500 text-white scale-110"
                                : runThroughCompleted.includes(q.question_id)
                                ? "bg-green-500 text-white"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                            }`}
                            title={`Question ${idx + 1}`}
                          >
                            {runThroughCompleted.includes(q.question_id) ? "✓" : idx + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Question Content */}
                  <div className="flex-1 overflow-auto p-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">
                      {/* Question Card */}
                      <div className="bg-card rounded-lg border p-4 overflow-auto">
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            runThroughQuestions[runThroughCurrentIndex]?.pool === "warmup" 
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
                              : "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400"
                          }`}>
                            {runThroughQuestions[runThroughCurrentIndex]?.pool === "warmup" ? "Warm Up" : "Challenge"}
                          </span>
                          <DifficultyStars difficulty={runThroughPane.drillDifficulty || 3} />
                        </div>
                        <h4 className="font-semibold text-lg mb-2">
                          {runThroughQuestions[runThroughCurrentIndex]?.title}
                        </h4>
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                            {runThroughQuestions[runThroughCurrentIndex]?.description}
                          </p>
                        </div>
                        {runThroughQuestions[runThroughCurrentIndex]?.hint && (
                          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-500/10 rounded-lg border border-amber-200 dark:border-amber-500/20">
                            <p className="text-sm text-amber-800 dark:text-amber-300">
                              <strong>Hint:</strong> {runThroughQuestions[runThroughCurrentIndex].hint}
                            </p>
                          </div>
                        )}
                        {runThroughQuestions[runThroughCurrentIndex]?.example_input && (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-medium">Example:</p>
                            <div className="bg-muted/50 rounded p-2 text-sm font-mono space-y-1">
                              <div>
                                <span className="text-muted-foreground">Input:</span>
                                <pre className="whitespace-pre-wrap mt-1 ml-2">{runThroughQuestions[runThroughCurrentIndex].example_input}</pre>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Output:</span>
                                <pre className="whitespace-pre-wrap mt-1 ml-2">{runThroughQuestions[runThroughCurrentIndex].example_output}</pre>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Code Editor (Blockly for Blocks questions) */}
                      <div className="flex flex-col bg-card rounded-lg border overflow-auto min-h-[300px] p-2" data-testid="run-through-editor">
                        <CodeWorkspace
                          key={runThroughQuestions[runThroughCurrentIndex]?.question_id}
                          question={runThroughQuestions[runThroughCurrentIndex]}
                          onSubmitSuccess={(result) => {
                            handleRunThroughQuestionComplete(
                              runThroughQuestions[runThroughCurrentIndex]?.question_id,
                              result
                            );
                          }}
                          isAdminPreview={true}
                          initialCode={runThroughCodeCache[runThroughQuestions[runThroughCurrentIndex]?.question_id]}
                          onCodeChange={(newCode) => {
                            const questionId = runThroughQuestions[runThroughCurrentIndex]?.question_id;
                            if (questionId) {
                              setRunThroughCodeCache(prev => ({
                                ...prev,
                                [questionId]: newCode
                              }));
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Navigation */}
                  <div className="p-3 border-t bg-muted/10 flex items-center justify-between">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={runThroughCurrentIndex === 0}
                      onClick={() => setRunThroughCurrentIndex(prev => prev - 1)}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <div className="text-sm text-muted-foreground">
                      {runThroughCompleted.length} of {runThroughQuestions.length} completed
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={runThroughCurrentIndex === runThroughQuestions.length - 1}
                      onClick={() => setRunThroughCurrentIndex(prev => prev + 1)}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Minimized: Click to expand */}
          {runThroughPane.minimized && (
            <div 
              className="absolute inset-0 w-full h-full rounded-lg cursor-pointer"
              onClick={toggleMinimizeRunThrough}
            />
          )}
        </div>
      )}

      {/* Backdrop when pane is open and not minimized */}
      {runThroughPane.open && !runThroughPane.minimized && (
        <div 
          className="fixed inset-0 bg-black/20 z-40"
          onClick={toggleMinimizeRunThrough}
        />
      )}

      {/* Student Recent Code Floating Pane */}
      {studentCodePane.open && (
        <div 
          className={`fixed z-50 bg-card border border-border rounded-lg shadow-2xl transition-all duration-300 ${
            studentCodePane.minimized
              ? "bottom-4 right-4 w-72 h-12"
              : "bottom-4 right-4 w-[500px] max-h-[70vh]"
          }`}
          data-testid="student-code-pane"
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-3 border-b bg-gradient-to-r from-purple-500/10 to-pink-500/10 ${
            studentCodePane.minimized ? "rounded-lg border-0" : "rounded-t-lg"
          }`}>
            <div className="flex items-center gap-2 min-w-0">
              <Code className="w-4 h-4 text-purple-500 shrink-0" />
              <span className="font-medium truncate">
                {studentCodePane.student?.nickname}'s Code
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={toggleMinimizeStudentCode}
                className="p-1.5 rounded hover:bg-muted/80 transition-colors"
                title={studentCodePane.minimized ? "Expand" : "Minimize"}
              >
                {studentCodePane.minimized ? (
                  <Maximize2 className="w-4 h-4" />
                ) : (
                  <Minimize2 className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={closeStudentCodePane}
                className="p-1.5 rounded hover:bg-muted/80 transition-colors text-muted-foreground hover:text-destructive"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          {!studentCodePane.minimized && (
            <div className="overflow-y-auto max-h-[calc(70vh-60px)]">
              {studentCodeLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : studentCodePane.submissions.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <Code className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No drill submissions yet</p>
                  <p className="text-sm mt-1">
                    This student hasn't submitted any code for drills assigned by you.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {studentCodePane.submissions.map((submission, index) => (
                    <div key={`${submission.question_id}-${index}`} className="p-3">
                      <button
                        onClick={() => setExpandedStudentCode(
                          expandedStudentCode === `${submission.question_id}-${index}` 
                            ? null 
                            : `${submission.question_id}-${index}`
                        )}
                        className="w-full text-left"
                        data-testid={`student-submission-${submission.question_id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-sm truncate pr-2">
                            {submission.question_title || "Question"}
                          </h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            submission.correct
                              ? "bg-green-500/10 text-green-600 dark:text-green-400"
                              : "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                          }`}>
                            {submission.passed}/{submission.total}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{submission.drill_title || "Drill"}</span>
                          <span>•</span>
                          <span>
                            {new Date(submission.submitted_at).toLocaleDateString('en-AU', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </button>

                      {expandedStudentCode === `${submission.question_id}-${index}` && (
                        <div className="mt-2 rounded-lg bg-muted/50 overflow-hidden">
                          <div className="px-2 py-1.5 bg-muted/80 text-xs text-muted-foreground font-medium flex items-center justify-between">
                            <span>Python</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(submission.code);
                                toast.success("Code copied!");
                              }}
                              className="flex items-center gap-1 hover:text-foreground transition-colors"
                            >
                              <Copy className="w-3 h-3" /> Copy
                            </button>
                          </div>
                          <pre className="p-2 text-xs font-mono overflow-x-auto max-h-[200px] overflow-y-auto">
                            <code>{submission.code}</code>
                          </pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Minimized: Click to expand */}
          {studentCodePane.minimized && (
            <div 
              className="absolute inset-0 w-full h-full rounded-lg cursor-pointer"
              onClick={toggleMinimizeStudentCode}
            />
          )}
        </div>
      )}

      {/* Backdrop when student code pane is open and not minimized */}
      {studentCodePane.open && !studentCodePane.minimized && (
        <div 
          className="fixed inset-0 bg-black/20 z-40"
          onClick={toggleMinimizeStudentCode}
        />
      )}
    </div>
  );
}
